import { Box } from "@mui/material";
import * as React from "react";
import { PointerEvent, useEffect, useRef, useState } from "react";

interface ClearCanvasProps {
  children: React.ReactNode;
  active?: boolean;
  onMouseMove?: (
    e: PointerEvent<HTMLCanvasElement>,
    ctx: CanvasRenderingContext2D
  ) => void;
  onDraw?: (ctx: CanvasRenderingContext2D, x: number, y: number) => void;
  onClick?: (
    e: PointerEvent<HTMLCanvasElement>,
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number
  ) => void;
  style?: React.CSSProperties;
  expandBy?: number;
}

export const ClearCanvasOverlay = React.forwardRef<
  HTMLCanvasElement,
  ClearCanvasProps
>(
  (
    { active, children, onMouseMove, onDraw, onClick, expandBy = 1, style },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [isDrawing, setIsDrawing] = useState(false);
    const pointerDownPos = useRef<{ x: number; y: number } | null>(null);

    useEffect(
      () => {
        // Update the dimensions of the canvas based on the child component
        const updateDimensions = () => {
          if (containerRef.current) {
            const { offsetWidth, offsetHeight } = containerRef.current;
            setDimensions({
              width: offsetWidth * expandBy,
              height: offsetHeight * expandBy,
            });
          }
        };

        updateDimensions(); // Update dimensions on mount
        window.addEventListener("resize", updateDimensions); // Adjust on window resize

        return () => window.removeEventListener("resize", updateDimensions);
      },
      // if the containerRef changes, update the dimensions
      [containerRef?.current?.offsetWidth, containerRef?.current?.offsetHeight]
    );

    useEffect(() => {
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        canvas.width = dimensions.width;
        canvas.height = dimensions.height;
      }
    }, [dimensions]);

    useEffect(() => {
      // Assign ref if a ref is passed from the parent
      if (ref && canvasRef.current) {
        if (typeof ref === "function") {
          ref(canvasRef.current);
        } else {
          (ref as React.MutableRefObject<HTMLCanvasElement>).current =
            canvasRef.current;
        }
      }
    }, [ref]);

    const handlePointerMove = (e: PointerEvent<HTMLCanvasElement>) => {
      // Ignore multi-touch gestures (only handle primary pointer)
      if (!e.isPrimary) return;

      // Prevent scrolling during drag on touch devices
      if (e.pointerType === 'touch' && isDrawing) {
        e.preventDefault();
      }

      if (onMouseMove && canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          onMouseMove(e, ctx);
        }
      }

      if (isDrawing && canvasRef.current && onDraw) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        if (ctx) {
          onDraw(ctx, x, y);
        }
      }
    };

    const handlePointerDown = (e: PointerEvent<HTMLCanvasElement>) => {
      // Ignore multi-touch gestures (only handle primary pointer)
      if (!e.isPrimary) return;

      // Prevent scrolling/zooming on touch devices
      if (e.pointerType === 'touch') {
        e.preventDefault();
      }

      // Store pointer down position for click detection
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        pointerDownPos.current = {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        };
      }

      setIsDrawing(true);

      // Capture pointer for reliable event tracking
      if (canvasRef.current) {
        try {
          canvasRef.current.setPointerCapture(e.pointerId);
        } catch (ex) {
          // Pointer capture not supported or failed
        }
      }
    };

    const handlePointerUp = (e: PointerEvent<HTMLCanvasElement>) => {
      if (!e.isPrimary) return;

      setIsDrawing(false);

      // Release pointer capture
      if (canvasRef.current) {
        try {
          canvasRef.current.releasePointerCapture(e.pointerId);
        } catch (ex) {
          // Capture not supported or already released
        }
      }

      // Detect if this is a click (not a drag) and trigger onClick callback
      if (onClick && pointerDownPos.current && canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        const upX = e.clientX - rect.left;
        const upY = e.clientY - rect.top;

        // Calculate distance moved since pointer down
        const dx = upX - pointerDownPos.current.x;
        const dy = upY - pointerDownPos.current.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // If moved less than 5px, treat as a click
        if (distance < 5) {
          const ctx = canvasRef.current.getContext("2d");
          if (ctx) {
            onClick(e, ctx, upX, upY);
          }
        }
      }

      // Clear pointer down position
      pointerDownPos.current = null;
    };

    return (
      <Box ref={containerRef} position="relative" display="inline-block">
        {/* Child Component */}
        {children}

        {/* Clear Canvas Overlay */}
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: active ? 100000 : -1, // Ensure the canvas is on top
            pointerEvents: active ? 'auto' : 'none', // Don't intercept events when inactive
            width: dimensions.width,
            height: dimensions.height,
            touchAction: 'none', // Prevent browser touch gestures (scroll, zoom, etc.)
            ...style,
          }}
          onPointerMove={handlePointerMove}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        />
      </Box>
    );
  }
);
